/**
 * Матрица прав «раздел × действие → область» для экрана «Доступы». Правка №11 (Чат 5). [Claude] 2026-11-02
 * Право — строка «объект:действие:область» (как в backend app/compliance/rbac.py).
 */

export const ACTION_ORDER = ["view", "create", "edit", "delete", "export", "approve"];
export const FIELD_ACTIONS = ["view", "edit"];
export const SCOPE_ORDER = ["own", "department", "all"];
export const SCOPE_LABELS = { "": "—", own: "Свои", department: "Отдела", all: "Все" };
const RANK = { own: 1, department: 2, all: 3 };

export function parseGrant(raw) {
    const [object, action, scope = "all"] = String(raw || "").split(":");
    return object && action ? { object, action, scope } : null;
}

/** grants → { object: { action: widestScope } } */
export function grantsToCells(grants) {
    const cells = {};
    for (const raw of grants || []) {
        const g = parseGrant(raw);
        if (!g) continue;
        const row = (cells[g.object] = cells[g.object] || {});
        if (!row[g.action] || RANK[g.scope] > RANK[row[g.action]]) row[g.action] = g.scope;
    }
    return cells;
}

/** cells → отсортированный список прав (по одной, самой широкой области на действие). */
export function cellsToGrants(cells) {
    const out = [];
    for (const [object, row] of Object.entries(cells || {})) {
        for (const [action, scope] of Object.entries(row || {})) {
            if (scope) out.push(`${object}:${action}:${scope}`);
        }
    }
    return out.sort();
}

export function setCell(cells, object, action, scope) {
    const next = { ...cells, [object]: { ...(cells[object] || {}) } };
    if (scope) next[object][action] = scope;
    else delete next[object][action];
    return next;
}

export function actionsFor(object, fields = []) {
    return fields.includes(object) ? FIELD_ACTIONS : ACTION_ORDER;
}

/**
 * Личные исключения: состояние ячейки относительно ролей.
 * inherit — как в роли; allow:<scope> — плюс; deny — минус (действие целиком).
 */
export function overrideState(object, action, allow, deny) {
    if ((deny || []).some((d) => d.startsWith(`${object}:${action}`))) return "deny";
    const a = (allow || []).map(parseGrant).find((g) => g && g.object === object && g.action === action);
    return a ? `allow:${a.scope}` : "inherit";
}

export function setOverride(allow, deny, object, action, state) {
    const key = `${object}:${action}`;
    const nextAllow = (allow || []).filter((g) => !g.startsWith(`${key}:`));
    const nextDeny = (deny || []).filter((d) => d !== key && !d.startsWith(`${key}:`));
    if (state === "deny") nextDeny.push(key);
    else if (state.startsWith("allow:")) nextAllow.push(`${key}:${state.slice(6)}`);
    return { allow: nextAllow.sort(), deny: nextDeny.sort() };
}

/** Короткое описание итоговых прав по группам: «Сотрудники: смотреть (все), менять (отдела)». */
export function describeGrants(grants, matrix) {
    const cells = grantsToCells(grants);
    const objects = matrix?.objects || {};
    const actions = matrix?.actions || {};
    const scopes = matrix?.scopes || SCOPE_LABELS;
    return Object.keys(cells)
        .filter((o) => Object.keys(cells[o]).length)
        .map((o) => ({
            object: o,
            label: objects[o] || o,
            text: ACTION_ORDER.filter((a) => cells[o][a])
                .map((a) => `${(actions[a] || a).toLowerCase()} (${(scopes[cells[o][a]] || cells[o][a]).toLowerCase()})`)
                .join(", "),
        }));
}

export function filterPeople(people, { query = "", erp = "all", noRole = false } = {}) {
    const q = query.trim().toLocaleLowerCase("ru-RU");
    return (people || []).filter((p) => {
        if (erp === "yes" && !p.has_erp) return false;
        if (erp === "no" && p.has_erp) return false;
        if (noRole && (p.roles || []).length) return false;
        if (!q) return true;
        return [p.full_name, p.department, p.position]
            .some((v) => String(v || "").toLocaleLowerCase("ru-RU").includes(q));
    });
}
